import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@clari/ui'
import { ErrorBanner } from '@/components/ErrorBanner'
import { apiErrorMessage } from '@/lib/api-error'
import { bankingApi } from '@/lib/banking-api'

const ACCEPT = '.pdf,.jpg,.jpeg,.png'

/** Upload a justification doc for a flagged transfer (USP #2). */
export function JustificationUpload({ assessmentId }: { assessmentId: string }) {
  const { t } = useTranslation('banking')
  const [file, setFile] = useState<File | null>(null)
  const [note, setNote] = useState('')
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function upload() {
    if (!file) return
    setError(null)
    setLoading(true)
    try {
      await bankingApi.uploadJustification(assessmentId, file, note || undefined)
      setDone(true)
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  if (done) return <p className="text-sm text-success">{t('justification.uploaded')}</p>

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border p-3">
      <p className="text-sm font-medium">{t('justification.title')}</p>
      <label className="text-xs text-text-muted">
        {t('justification.note')}
        <textarea
          className="clari-input mt-1 w-full"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      <label className="text-xs text-text-muted">
        {t('justification.file')}
        <input
          className="mt-1 block w-full text-sm"
          type="file"
          accept={ACCEPT}
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
      </label>
      {error ? <ErrorBanner message={error} /> : null}
      <Button variant="secondary" onClick={upload} disabled={loading || !file}>
        {t('justification.upload')}
      </Button>
    </div>
  )
}
